import {useMessage} from '@coolcolduk/ui-mui';
import {randomArray} from '@coolcolduk/util';
import {Close, Error as ErrorIcon, ExpandMore} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useCallback, useState} from 'react';
import {z} from 'zod';
import {ExamModule, ModuleTopic} from '@cp/model';
import {useCreateQuestion} from '../../../hooks/question/useCreateQuestion';

// Schema for extracted questions (includes difficulty)
const ExtractedQuestionSchema = z.object({
  questionText: z.string(),
  hint: z.string(),
  incorrectAnswers: z.array(z.string()),
  correctAnswer: z.string(),
  explanation: z.string().refine((val) => !val.toLowerCase().includes('no, wait'), {
    message: 'Explanation must not contain the string "no, wait"',
  }),
  difficulty: z.number().int().min(1).max(5),
});

type ExtractedQuestion = z.infer<typeof ExtractedQuestionSchema>;

interface FailedQuestion {
  data: unknown;
  error: string;
}

export interface AdminStudyQuestionsImportProps {
  selectedModule?: ExamModule;
  selectedTopic?: ModuleTopic;
}

export default function AdminStudyQuestionsImport({selectedModule, selectedTopic}: AdminStudyQuestionsImportProps) {
  const [jsonlText, setJsonlText] = useState('');
  const [importing, setImporting] = useState(false);
  const [failedQuestions, setFailedQuestions] = useState<FailedQuestion[]>([]);
  const [showErrorLog, setShowErrorLog] = useState(false);
  const {showMessage} = useMessage();
  const {createQuestion} = useCreateQuestion();

  const parseJsonl = useCallback((text: string): {valid: ExtractedQuestion[]; failed: FailedQuestion[]} => {
    const lines = text.split('\n').filter((line) => line.trim());
    const valid: ExtractedQuestion[] = [];
    const failed: FailedQuestion[] = [];

    lines.forEach((line, index) => {
      try {
        // Fix common JSON issues
        const fixedLine = line.replace(/ rac{/g, '\\\\frac{');
        let parsed: unknown;

        try {
          parsed = JSON.parse(fixedLine);
        } catch {
          // Try with double slashes
          parsed = JSON.parse(fixedLine.replace(/\\/g, '\\\\'));
        }

        const validated = ExtractedQuestionSchema.safeParse(parsed);
        if (validated.success) {
          valid.push(validated.data);
        } else {
          failed.push({
            data: parsed,
            error: `Line ${index + 1}: ${validated.error.issues.map((e: any) => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
          });
        }
      } catch (error) {
        failed.push({
          data: line,
          error: `Line ${index + 1}: ${error instanceof Error ? error.message : 'Invalid JSON'}`,
        });
      }
    });

    return {valid, failed};
  }, []);

  const mapToQuestionInput = useCallback(
    (question: ExtractedQuestion) => {
      // Filter out correct answer from incorrect answers
      const incorrectAnswersWithoutCorrect = question.incorrectAnswers.filter(
        (answer) => answer !== question.correctAnswer,
      );

      // Combine and shuffle options
      const allAnswers = [question.correctAnswer, ...incorrectAnswersWithoutCorrect].filter((a) => a && a.trim());
      const uniqueAnswers = Array.from(new Set(allAnswers));
      if (!uniqueAnswers.includes(question.correctAnswer)) {
        uniqueAnswers.unshift(question.correctAnswer);
      }
      const options = randomArray(uniqueAnswers);

      return {
        moduleId: selectedModule!.id,
        topic: selectedTopic!.topic,
        difficulty: question.difficulty,
        questionText: question.questionText,
        options,
        answer: question.correctAnswer,
        explanation: question.explanation,
        hint: question.hint || undefined,
      };
    },
    [selectedModule, selectedTopic],
  );

  const handleImport = useCallback(async () => {
    if (!selectedModule || !selectedTopic) {
      showMessage('Please select a module and topic', 6000, 'error');
      return;
    }

    if (!jsonlText.trim()) {
      showMessage('Please paste questions in JSONL format', 6000, 'error');
      return;
    }

    setImporting(true);
    setFailedQuestions([]);

    try {
      // Parse and validate all questions first
      const {valid: questions, failed: parseFailed} = parseJsonl(jsonlText);
      const importFailed: FailedQuestion[] = [...parseFailed];

      // Import each valid question
      let successCount = 0;
      for (const question of questions) {
        try {
          const questionInput = mapToQuestionInput(question);
          await createQuestion(
            questionInput.moduleId,
            questionInput.topic,
            questionInput.difficulty,
            questionInput.questionText,
            questionInput.options,
            questionInput.answer,
            questionInput.explanation,
            questionInput.hint,
          );
          successCount++;
        } catch (error) {
          importFailed.push({
            data: question,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }

      if (importFailed.length > 0) {
        setFailedQuestions(importFailed);
        showMessage(
          `Imported ${successCount} questions. ${importFailed.length} failed. Click "View Errors" to see details.`,
          8000,
          'warning',
        );
      } else {
        showMessage(`Successfully imported ${successCount} questions`, 6000, 'success');
        setJsonlText('');
      }
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Failed to process questions', 6000, 'error');
    } finally {
      setImporting(false);
    }
  }, [jsonlText, selectedModule, selectedTopic, parseJsonl, mapToQuestionInput, createQuestion, showMessage]);

  if (!selectedModule || !selectedTopic) {
    return null;
  }

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{fontWeight: 600}}>
            Questions
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={10}
              label="Questions (JSONL Format)"
              placeholder="Paste questions in JSONL format here. Each line should be a JSON object with: questionText, hint, incorrectAnswers, correctAnswer, explanation, difficulty"
              value={jsonlText}
              onChange={(e) => setJsonlText(e.target.value)}
              helperText="Each line should be a minified JSON object. Questions will be imported with GENERATED status."
            />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button variant="contained" onClick={handleImport} disabled={importing || !jsonlText.trim()}>
                {importing ? 'Importing...' : 'Import Questions'}
              </Button>
              {failedQuestions.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<ErrorIcon />}
                  onClick={() => setShowErrorLog(true)}
                  color="warning"
                >
                  View Errors ({failedQuestions.length})
                </Button>
              )}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Dialog open={showErrorLog} onClose={() => setShowErrorLog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Import Errors ({failedQuestions.length})</Typography>
            <IconButton onClick={() => setShowErrorLog(false)}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {failedQuestions.map((failed, index) => (
              <Stack
                key={`failed-${failed.error.substring(0, 20)}`}
                spacing={1}
                sx={{p: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 1}}
              >
                <Typography variant="subtitle2" color="error">
                  Error {index + 1}: {failed.error}
                </Typography>
                <Typography variant="body2" component="pre" sx={{overflow: 'auto', fontSize: '0.75rem'}}>
                  {JSON.stringify(failed.data, null, 2)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
